import {openDeleteDialog, showNotification} from 'platform/components';
import {match} from 'ts-pattern';

// eslint-disable-next-line no-restricted-imports -- Intended
import {head, isNil} from 'ramda';
import {isArray} from 'ramda-adjunct';

import {
  useDeleteServiceOrderIssueNoteMutation,
  usePatchServiceOrderIssueNotePartsReadyMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {testIds, warehouseRoutes} from '@dms/routes';
import {DmsDataGrid, catchUnhandledDataGridActions, handleApiError, logError} from '@dms/shared';
import {FullscreenDataGridWrapper} from '@dms/warehouse/shared-components';

import {composePath, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

const PREDEFINED_COLUMNS = ['partsReady'] as const;

export function ServiceOrderIssues() {
  const navigate = useNavigate();

  const [partsReadyServiceOrderIssueNote] = usePatchServiceOrderIssueNotePartsReadyMutation();

  const [deleteServiceOrderIssueNote] = useDeleteServiceOrderIssueNoteMutation();

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = async ({
    actionKey,
    rowId,
    sourceSearchParams,
    refreshData,
    getColumnData,
  }) => {
    const serviceOrderIssueNoteId = isArray(rowId) ? head(rowId) : rowId;

    if (isNil(serviceOrderIssueNoteId)) {
      return logError('Service order issue note id is not defined');
    }

    await match(actionKey)
      .with('redirectDetail', 'edit', () =>
        navigate(
          composePath(warehouseRoutes.serviceOrderIssueDetailOverview, {
            queryParams: sourceSearchParams,
            params: {
              id: serviceOrderIssueNoteId,
            },
            isSearchParamsPreserved: false,
          })
        )
      )
      .with('ready', 'remove-ready', async () => {
        const arePartsReady = getColumnData<boolean>('partsReady')?.value;

        if (isNil(arePartsReady)) {
          return logError('Parts ready is not defined');
        }

        await partsReadyServiceOrderIssueNote({
          serviceOrderIssueNoteId,
          body: {partsReady: actionKey === 'ready'},
        })
          .unwrap()
          .then(() => refreshData())
          .catch(handleApiError);
      })
      .with('delete', () =>
        openDeleteDialog({
          onConfirm: async () =>
            await deleteServiceOrderIssueNote({serviceOrderIssueNoteId})
              .unwrap()
              .then(() =>
                showNotification.success(i18n.t('general.notifications.successfullyDeleted'))
              )
              .then(() => refreshData())
              .catch(handleApiError),
          'data-testid': testIds.warehouse.serviceOrderIssues('deleteDialog'),
        })
      )
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  return (
    <FullscreenDataGridWrapper hideCardWrapper>
      <DmsDataGrid
        gridCode="warehouse-service-order-issue-notes"
        actionCallback={actionCallback}
        data-testid={testIds.warehouse.serviceOrderIssues('list')}
        predefinedColumns={PREDEFINED_COLUMNS}
      />
    </FullscreenDataGridWrapper>
  );
}
